import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-compliance-visas',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="p-5 min-h-full bg-[#F7FAFC] overflow-y-auto">
      <div class="max-w-[1500px] mx-auto">
        <div class="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div>
            <h1 class="text-xl font-semibold text-slate-900 tracking-tight">Visa Tracker</h1>
            <p class="text-slate-500 text-xs">Track staff visa type, number, expiry, evidence, and work conditions.</p>
          </div>
          <button type="button" (click)="openVisaModal()" class="h-10 px-4 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 flex items-center gap-2 cursor-pointer" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
            <i class="ti ti-plus text-base"></i> Add Visa Record
          </button>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <div class="summary-card"><p>Tracked Staff</p><strong>{{ latestRecords().length }}</strong></div>
          <div class="summary-card"><p>Current</p><strong class="text-emerald-700">{{ currentCount() }}</strong></div>
          <div class="summary-card"><p>Expiring Soon</p><strong class="text-amber-700">{{ expiringCount() }}</strong></div>
          <div class="summary-card"><p>Expired</p><strong class="text-rose-700">{{ expiredCount() }}</strong></div>
        </div>

        <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm mb-4">
          <div class="px-4 py-3 flex flex-wrap items-center gap-3">
            <div class="relative flex-1 min-w-[240px] max-w-lg">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
              <input type="text" placeholder="Search staff, email, visa type, visa number..." [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)"
                     class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:outline-none focus:bg-white focus:border-[#1A6FA6] placeholder:text-slate-400">
            </div>
            <select [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)" class="h-9 px-3 rounded-lg text-xs border border-slate-200 bg-slate-50 text-slate-600 focus:outline-none cursor-pointer">
              <option value="">All Status</option>
              <option value="current">Current</option>
              <option value="expiring">Expiring Soon</option>
              <option value="expired">Expired</option>
              <option value="no_visa">No Visa Required</option>
            </select>
          </div>
        </div>

        <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div class="hidden lg:grid grid-cols-[1.4fr_1.2fr_1fr_0.8fr_1.1fr_1.2fr] gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-widest text-slate-400">
            <span>Staff</span><span>Visa</span><span>Number</span><span>Expiry</span><span>Status</span><span class="text-right">Actions</span>
          </div>

          @if (isLoading()) {
            <div class="p-8 text-center text-sm text-slate-500"><i class="ti ti-loader animate-spin"></i> Loading visa records...</div>
          } @else {
            @for (record of filteredLatestRecords(); track record.trackId) {
              <div class="grid grid-cols-1 lg:grid-cols-[1.4fr_1.2fr_1fr_0.8fr_1.1fr_1.2fr] gap-3 px-4 py-3 border-b border-slate-100 items-center hover:bg-slate-50/70">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-slate-900 truncate">{{ record.staff_name }}</p>
                  <p class="text-xs text-slate-500 truncate">{{ record.staff_email }}</p>
                  @if (record.all_visas?.length > 1) {
                    <p class="text-[10px] text-slate-400 font-semibold mt-0.5">{{ record.all_visas.length }} visa records</p>
                  }
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-slate-800 truncate">{{ record.visa_type }}</p>
                  @if (record.work_rights_notes) {
                    <p class="text-xs text-slate-500 truncate">{{ record.work_rights_notes }}</p>
                  }
                </div>
                <p class="text-sm font-semibold text-slate-700">{{ maskedVisaNumber(record) }}</p>
                <p class="text-sm font-semibold text-slate-700">{{ record.visa_expiry_date ? (record.visa_expiry_date | date:'d MMM y') : 'Not set' }}</p>
                <div class="flex flex-wrap gap-1.5">
                  <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border" [ngClass]="statusClass(record)">{{ statusLabel(record) }}</span>
                  @if (record.verified_at) {
                    <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-100">
                      <i class="ti ti-shield-check"></i> Verified
                    </span>
                  } @else {
                    <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border bg-slate-50 text-slate-500 border-slate-100">
                      Unverified
                    </span>
                  }
                </div>
                <div class="flex flex-wrap justify-start lg:justify-end gap-1.5">
                  <button type="button" (click)="openViewModal(record)" class="h-8 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold"><i class="ti ti-eye"></i> View</button>
                  @if (record.file_url) {
                    <a [href]="visaFileUrl(record)" target="_blank" rel="noopener" class="h-8 px-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold inline-flex items-center gap-1"><i class="ti ti-file"></i> Open Document</a>
                  }
                  <button type="button" (click)="openVisaModal(record)" class="h-8 px-3 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] text-xs font-semibold"><i class="ti ti-pencil"></i> Edit</button>
                  @if (record.id) {
                    <button type="button" (click)="deleteVisaRecord(record)" class="h-8 px-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-xs font-semibold"><i class="ti ti-trash"></i> Delete</button>
                  }
                </div>
              </div>
            } @empty {
              <div class="p-10 text-center">
                <div class="w-14 h-14 bg-[#EAF5FA] text-[#1A6FA6] rounded-xl flex items-center justify-center mx-auto mb-3">
                  <i class="ti ti-id-badge-2 text-2xl"></i>
                </div>
                <p class="text-sm font-semibold text-slate-900">No visa records found</p>
                <p class="text-xs text-slate-500 mt-1">Add a visa record for a staff member to start tracking it here.</p>
              </div>
            }
          }
        </div>

        @if (showViewModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">{{ viewStaffName() }}</h2>
                  <p class="text-xs text-slate-500 mt-1">{{ selectedViewVisas().length }} visa record(s)</p>
                </div>
                <button type="button" (click)="closeViewModal()" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500"><i class="ti ti-x text-lg"></i></button>
              </div>

              <div class="px-5 pt-4 border-b border-slate-100 flex gap-2 overflow-x-auto">
                @for (visa of selectedViewVisas(); track visa.id) {
                  <button type="button" (click)="setActiveViewVisa(visa)"
                          class="px-3 py-2 rounded-t-lg text-xs font-semibold border border-b-0 whitespace-nowrap"
                          [ngClass]="isActiveViewVisa(visa) ? 'bg-white text-[#1A6FA6] border-slate-200' : 'bg-slate-50 text-slate-500 border-transparent hover:bg-slate-100'">
                    {{ visa.is_current == 1 ? 'Current' : 'Past' }} - {{ visa.visa_type || 'Visa' }}
                  </button>
                }
              </div>

              @if (activeViewVisa(); as visa) {
                <div class="p-5 overflow-y-auto space-y-4">
                  <div class="flex flex-wrap gap-2">
                    <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border" [ngClass]="statusClass(visa)">{{ statusLabel(visa) }}</span>
                    @if (visa.verified_at) {
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-100"><i class="ti ti-shield-check"></i> Verified</span>
                    } @else {
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border bg-slate-50 text-slate-500 border-slate-100">Unverified</span>
                    }
                    @if (visa.is_current == 1) {
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border bg-sky-50 text-sky-700 border-sky-100">Current record</span>
                    }
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div class="detail-box"><p>Visa Type</p><strong>{{ visa.visa_type || '-' }}</strong></div>
                    <div class="detail-box"><p>Visa Number</p><strong>{{ maskedVisaNumber(visa) }}</strong></div>
                    <div class="detail-box"><p>Expiry Date</p><strong>{{ visa.visa_expiry_date ? (visa.visa_expiry_date | date:'d MMM y') : 'Not set' }}</strong></div>
                    <div class="detail-box"><p>Verified By</p><strong>{{ visa.verified_by_name || (visa.verified_at ? 'Verified' : 'Not verified') }}</strong></div>
                    <div class="detail-box"><p>Created By</p><strong>{{ visa.created_by_name || '-' }}</strong></div>
                    <div class="detail-box"><p>Created</p><strong>{{ visa.created_at ? (visa.created_at | date:'d MMM y, h:mm a') : '-' }}</strong></div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div class="detail-box min-h-[92px]"><p>Work Rights / Conditions</p><strong class="whitespace-pre-line">{{ visa.work_rights_notes || '-' }}</strong></div>
                    <div class="detail-box min-h-[92px]"><p>Notes</p><strong class="whitespace-pre-line">{{ visa.notes || '-' }}</strong></div>
                  </div>

                  <div class="flex flex-wrap gap-2">
                    @if (visa.file_url) {
                      <a [href]="visaFileUrl(visa)" target="_blank" rel="noopener" class="h-9 px-3 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] text-xs font-semibold inline-flex items-center gap-1"><i class="ti ti-file"></i> Open Visa Document</a>
                    }
                    <button type="button" (click)="openVisaModal(visa); closeViewModal()" class="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold"><i class="ti ti-pencil"></i> Edit This Record</button>
                    <button type="button" (click)="deleteVisaRecord(visa); closeViewModal()" class="h-9 px-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-xs font-semibold"><i class="ti ti-trash"></i> Delete</button>
                  </div>
                </div>
              }
            </div>
          </div>
        }

        @if (showVisaModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">{{ editingVisaId ? 'Edit Visa Record' : 'Add Visa Record' }}</h2>
                  <p class="text-xs text-slate-500 mt-1">Save visa details against a staff profile.</p>
                </div>
                <button type="button" (click)="closeVisaModal()" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500"><i class="ti ti-x text-lg"></i></button>
              </div>

              <div class="p-5 overflow-y-auto space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Staff Member</span>
                    <select [(ngModel)]="visaForm.user_id" class="field-input">
                      <option value="">Select staff</option>
                      @for (staff of staffList(); track staff.id) {
                        <option [value]="staff.id">{{ staff.name }} - {{ staff.roleLabel }}</option>
                      }
                    </select>
                  </label>
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Visa Type</span>
                    <select [(ngModel)]="visaForm.visa_type" class="field-input">
                      @for (type of visaTypes; track type) {
                        <option [value]="type">{{ type }}</option>
                      }
                    </select>
                  </label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Visa Number</span><input type="text" [(ngModel)]="visaForm.visa_number" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Visa Expiry</span><input type="date" [(ngModel)]="visaForm.visa_expiry_date" class="field-input"></label>
                  <label class="block md:col-span-2"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Work Rights / Conditions</span><textarea [(ngModel)]="visaForm.work_rights_notes" rows="2" class="field-input h-auto py-2 resize-none"></textarea></label>
                  <label class="block md:col-span-2"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Notes</span><textarea [(ngModel)]="visaForm.notes" rows="2" class="field-input h-auto py-2 resize-none"></textarea></label>
                </div>

                <div class="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <label class="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 cursor-pointer inline-flex items-center gap-2">
                    <i class="ti ti-upload"></i> Upload Visa Document
                    <input type="file" accept=".pdf,image/jpeg,image/png,image/webp" class="hidden" (change)="onVisaCopySelected($event)">
                  </label>
                  @if (visaCopyFileName) {
                    <span class="text-xs font-semibold text-slate-600">{{ visaCopyFileName }}</span>
                  } @else if (visaForm.file_url) {
                    <a [href]="visaFileUrl(visaForm)" target="_blank" rel="noopener" class="text-xs font-semibold text-[#1A6FA6] hover:underline">Open current document</a>
                  }
                  @if (visaForm.file_url) {
                    <button type="button" (click)="visaForm.remove_file = true; visaForm.file_url = ''" class="h-8 px-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-xs font-semibold">Remove Document</button>
                  }
                  <label class="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 flex items-center gap-2 text-xs font-semibold cursor-pointer">
                    <input type="checkbox" [(ngModel)]="visaForm.is_current" class="rounded border-slate-300">
                    Current
                  </label>
                  <label class="h-9 px-3 rounded-lg border border-emerald-100 bg-emerald-50 text-emerald-700 flex items-center gap-2 text-xs font-semibold cursor-pointer">
                    <input type="checkbox" [(ngModel)]="visaForm.verified" class="rounded border-emerald-300">
                    Verified
                  </label>
                </div>
              </div>

              <div class="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                <button type="button" (click)="closeVisaModal()" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200">Cancel</button>
                <button type="button" (click)="saveVisa()" [disabled]="isSavingVisa() || !visaForm.user_id || !visaForm.visa_type" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50 flex items-center gap-2">
                  @if (isSavingVisa()) { <i class="ti ti-loader animate-spin"></i> Saving... } @else { Save Visa }
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .summary-card {
      border: 1px solid #E7EEF5;
      border-radius: 0.75rem;
      background: #fff;
      padding: 0.75rem 1rem;
    }
    .summary-card p {
      font-size: 0.625rem;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      font-weight: 700;
      color: #94a3b8;
    }
    .summary-card strong {
      display: block;
      margin-top: 0.25rem;
      font-size: 1.25rem;
      font-weight: 700;
      color: #0f172a;
    }
    .field-input {
      width: 100%;
      height: 2.5rem;
      border-radius: 0.5rem;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      padding: 0 0.75rem;
      font-size: 0.875rem;
      color: #1e293b;
      outline: none;
      transition: border-color 160ms ease, background 160ms ease;
    }
    .field-input:focus {
      border-color: #1A6FA6;
      background: #fff;
    }
    .detail-box {
      border: 1px solid #e2e8f0;
      border-radius: 0.75rem;
      background: #f8fafc;
      padding: 0.75rem;
    }
    .detail-box p {
      font-size: 0.625rem;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      font-weight: 700;
      color: #94a3b8;
      margin-bottom: 0.35rem;
    }
    .detail-box strong {
      display: block;
      font-size: 0.875rem;
      color: #0f172a;
      font-weight: 650;
    }
  `]
})
export class AdminComplianceVisasComponent implements OnInit {
  private api = inject(ApiService);

  staffList = signal<any[]>([]);
  visaRecords = signal<any[]>([]);
  isLoading = signal(true);
  isSavingVisa = signal(false);
  showVisaModal = signal(false);
  showViewModal = signal(false);
  selectedViewVisas = signal<any[]>([]);
  activeViewVisaId = signal<number | null>(null);
  viewStaffName = signal('');
  searchQuery = signal('');
  statusFilter = signal('');

  editingVisaId: number | null = null;
  visaCopyFile: File | null = null;
  visaCopyFileName = '';
  visaForm: any = this.emptyVisaForm();
  visaTypes = [
    'NZ Citizen / No visa required',
    'Resident Visa',
    'Permanent Resident Visa',
    'Accredited Employer Work Visa',
    'Post Study Work Visa',
    'Working Holiday Visa',
    'Specific Purpose Work Visa',
    'Partner of Worker Work Visa',
    'Partner of New Zealander Work/Resident Visa',
    'Student Visa with work rights',
    'Interim Visa',
    'Other'
  ];

  latestRecords = computed(() => this.latestVisaRecords(this.visaRecords()));
  currentCount = computed(() => this.latestRecords().filter(record => record.id && this.statusKey(record) === 'current').length);
  expiringCount = computed(() => this.latestRecords().filter(record => this.statusKey(record) === 'expiring').length);
  expiredCount = computed(() => this.latestRecords().filter(record => this.statusKey(record) === 'expired').length);

  filteredLatestRecords = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const status = this.statusFilter();
    return this.latestRecords().filter(record => {
      const queryMatches = !query || [
        record.staff_name, record.staff_email, record.visa_type, record.visa_number, record.work_rights_notes,
        ...(record.all_visas || []).flatMap((visa: any) => [visa.visa_type, visa.visa_number, visa.work_rights_notes, visa.notes])
      ].some(value => String(value || '').toLowerCase().includes(query));
      const statusMatches = !status || this.statusKey(record) === status;
      return queryMatches && statusMatches;
    });
  });

  ngOnInit() {
    this.loadStaff();
  }

  loadStaff() {
    this.isLoading.set(true);
    this.api.getStaff().subscribe({
      next: (res: any) => {
        const users = res.success && res.data?.users ? res.data.users : [];
        const staff = users.map((u: any) => ({
          ...u,
          roleLabel: u.role === 'Worker' ? 'Plumber / Field Worker' : (Number(u.can_work_as_worker || u.has_worker_access) ? `${u.role} + Field` : u.role),
          visas: Array.isArray(u.visas) ? u.visas : []
        }));
        this.staffList.set(staff);
        this.visaRecords.set(this.flattenVisaRecords(staff));
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        showToast('error', 'Failed to load staff visa records.');
      }
    });
  }

  openVisaModal(record: any = null) {
    this.editingVisaId = record?.id ? Number(record.id) : null;
    this.visaCopyFile = null;
    this.visaCopyFileName = '';
    this.visaForm = record ? {
      user_id: String(record.user_id || ''),
      visa_type: record.visa_type || this.visaTypes[0],
      visa_number: record.visa_number || '',
      visa_expiry_date: record.visa_expiry_date || '',
      file_url: record.file_url || '',
      work_rights_notes: record.work_rights_notes || '',
      notes: record.notes || '',
      is_current: Number(record.is_current) === 1 || !record.id,
      verified: !!record.verified_at,
      remove_file: false
    } : this.emptyVisaForm();
    this.showVisaModal.set(true);
  }

  closeVisaModal() {
    this.showVisaModal.set(false);
    this.editingVisaId = null;
    this.visaCopyFile = null;
    this.visaCopyFileName = '';
    this.visaForm = this.emptyVisaForm();
  }

  openViewModal(record: any) {
    const visas = this.sortVisaRecords(record.all_visas?.length ? record.all_visas : [record]);
    this.selectedViewVisas.set(visas);
    this.activeViewVisaId.set(Number(record.id || visas[0]?.id || 0));
    this.viewStaffName.set(record.staff_name || 'Visa Details');
    this.showViewModal.set(true);
  }

  closeViewModal() {
    this.showViewModal.set(false);
    this.selectedViewVisas.set([]);
    this.activeViewVisaId.set(null);
    this.viewStaffName.set('');
  }

  activeViewVisa(): any | null {
    const id = Number(this.activeViewVisaId() || 0);
    return this.selectedViewVisas().find(visa => Number(visa.id) === id) || this.selectedViewVisas()[0] || null;
  }

  setActiveViewVisa(visa: any) {
    this.activeViewVisaId.set(Number(visa?.id || 0));
  }

  isActiveViewVisa(visa: any): boolean {
    return Number(this.activeViewVisaId() || 0) === Number(visa?.id || 0);
  }

  onVisaCopySelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('error', 'Visa document must be a PDF, JPG, PNG, or WebP file.');
      input.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      showToast('error', 'Visa document must be under 8MB.');
      input.value = '';
      return;
    }

    this.visaCopyFile = file;
    this.visaCopyFileName = file.name;
    this.visaForm.remove_file = false;
  }

  saveVisa() {
    if (!this.visaForm.user_id || !this.visaForm.visa_type) return;

    this.isSavingVisa.set(true);
    const formData = new FormData();
    if (this.editingVisaId) formData.append('id', String(this.editingVisaId));
    formData.append('user_id', String(this.visaForm.user_id));
    formData.append('visa_type', this.visaForm.visa_type || '');
    formData.append('visa_number', this.visaForm.visa_number || '');
    formData.append('visa_expiry_date', this.visaForm.visa_expiry_date || '');
    formData.append('work_rights_notes', this.visaForm.work_rights_notes || '');
    formData.append('notes', this.visaForm.notes || '');
    formData.append('is_current', this.visaForm.is_current ? '1' : '0');
    formData.append('verified', this.visaForm.verified ? '1' : '0');
    formData.append('remove_file', this.visaForm.remove_file ? '1' : '0');
    if (this.visaCopyFile) formData.append('visa_copy', this.visaCopyFile);

    this.api.saveStaffVisa(formData).subscribe({
      next: (res: any) => {
        this.isSavingVisa.set(false);
        if (res.success) {
          showToast('success', 'Visa record saved.');
          this.closeVisaModal();
          this.loadStaff();
        } else {
          showToast('error', res.message || 'Failed to save visa record.');
        }
      },
      error: (err) => {
        this.isSavingVisa.set(false);
        showToast('error', err?.error?.message || 'Network error while saving visa record.');
      }
    });
  }

  deleteVisaRecord(record: any) {
    if (!record?.id) return;
    const confirmed = window.confirm(`Delete this ${record.visa_type || 'visa'} record for ${record.staff_name}?`);
    if (!confirmed) return;

    this.api.deleteStaffVisa(Number(record.id)).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Visa record deleted.');
          this.loadStaff();
        } else {
          showToast('error', res.message || 'Failed to delete visa record.');
        }
      },
      error: (err) => showToast('error', err?.error?.message || 'Network error while deleting visa record.')
    });
  }

  statusLabel(record: any): string {
    const labels: Record<string, string> = {
      current: 'Current',
      expiring: 'Expiring soon',
      expired: 'Expired',
      missing: 'Missing',
      no_visa: 'No visa required'
    };
    return labels[this.statusKey(record)] || 'Current';
  }

  statusClass(record: any): string {
    const key = this.statusKey(record);
    if (key === 'expired' || key === 'missing') return 'bg-rose-50 text-rose-700 border-rose-100';
    if (key === 'expiring') return 'bg-amber-50 text-amber-700 border-amber-100';
    if (key === 'no_visa') return 'bg-slate-50 text-slate-600 border-slate-100';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }

  maskedVisaNumber(record: any): string {
    const value = String(record?.visa_number || '').trim();
    if (!value) return 'Not recorded';
    if (value.length <= 4) return value;
    return `${'*'.repeat(Math.max(0, value.length - 4))}${value.slice(-4)}`;
  }

  visaFileUrl(record: any): string {
    return this.api.assetUrl(record?.file_url || '');
  }

  private flattenVisaRecords(staff: any[]): any[] {
    const records: any[] = [];
    for (const member of staff) {
      for (const visa of member.visas) {
        records.push({
          ...visa,
          trackId: `visa-${visa.id}`,
          staff_name: member.name,
          staff_email: member.email,
          roleLabel: member.roleLabel
        });
      }
    }
    return records;
  }

  private latestVisaRecords(records: any[]): any[] {
    const grouped = new Map<number, any[]>();
    for (const record of records) {
      const userId = Number(record.user_id || 0);
      if (!userId) continue;
      const current = grouped.get(userId) || [];
      current.push(record);
      grouped.set(userId, current);
    }

    return Array.from(grouped.values()).map(group => {
      const sorted = this.sortVisaRecords(group);
      return {
        ...sorted[0],
        all_visas: sorted
      };
    });
  }

  private sortVisaRecords(records: any[]): any[] {
    return [...records].sort((a, b) => {
      const currentDiff = Number(b.is_current || 0) - Number(a.is_current || 0);
      if (currentDiff !== 0) return currentDiff;
      const expiryDiff = this.dateTimeValue(b.visa_expiry_date) - this.dateTimeValue(a.visa_expiry_date);
      if (expiryDiff !== 0) return expiryDiff;
      return this.dateTimeValue(b.created_at) - this.dateTimeValue(a.created_at);
    });
  }

  private dateTimeValue(value: string): number {
    if (!value) return 0;
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : 0;
  }

  private statusKey(record: any): string {
    if (this.isNoVisaRequired(record)) return 'no_visa';
    if (!record.visa_expiry_date) return 'missing';
    const days = this.daysUntil(record.visa_expiry_date);
    if (days < 0) return 'expired';
    if (days <= 60) return 'expiring';
    return 'current';
  }

  private isNoVisaRequired(record: any): boolean {
    const type = String(record?.visa_type || '').toLowerCase();
    return type.includes('no visa required') || type.includes('nz citizen');
  }

  private daysUntil(dateValue: string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateValue);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target.getTime() - today.getTime()) / 86400000);
  }

  private emptyVisaForm(): any {
    return {
      user_id: '',
      visa_type: 'NZ Citizen / No visa required',
      visa_number: '',
      visa_expiry_date: '',
      file_url: '',
      work_rights_notes: '',
      notes: '',
      is_current: true,
      verified: false,
      remove_file: false
    };
  }
}
